import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { UserTypeahead } from '../components/UserTypeahead';
import { MetricCard, PageHeader, PageShell, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';

type UserDto = { id: string; email: string; firstName?: string | null; lastName?: string | null };
type GroupDto = { id: string; name: string; description?: string | null; groupType?: 'STANDARD' | 'KINDERGARTEN' | 'SCHOOL' };
type CommunityDto = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  users: UserDto[];
  groups: GroupDto[];
  managers: UserDto[];
};

export const CommunityDetailPage: React.FC = () => {
  const { id } = useParams();
  const { request } = useApiClient();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { t } = useI18n();
  const [data, setData] = useState<CommunityDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState<UserDto[]>([]);
  const [pendingManagers, setPendingManagers] = useState<UserDto[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState('STANDARD');
  const [communityName, setCommunityName] = useState('');
  const [communityDescription, setCommunityDescription] = useState('');
  const [communityType, setCommunityType] = useState('COMPANY');
  const [savingCommunity, setSavingCommunity] = useState(false);

  useEffect(() => {
    if (!id) return;
    request<CommunityDto>(`/communities/${id}`)
      .then((community) => {
        setData(community);
        setCommunityName(community.name);
        setCommunityDescription(community.description ?? '');
        setCommunityType(community.type);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [id, request]);

  async function saveCommunity(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;

    setSavingCommunity(true);
    try {
      const updated = await request<CommunityDto>(`/communities/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: communityName,
          description: communityDescription.trim() ? communityDescription : null,
          type: communityType,
        }),
      });
      setData(updated);
      setCommunityName(updated.name);
      setCommunityDescription(updated.description ?? '');
      setCommunityType(updated.type);
      showSuccess(t('communities.updated'));
    } catch (err: any) {
      showError(err?.message || 'Failed to update community');
    } finally {
      setSavingCommunity(false);
    }
  }

  async function addUsers(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    const ids = pendingUsers.map((u) => u.id);
    try {
      const updated = await request<CommunityDto>(`/communities/${id}/users`, {
        method: 'POST',
        body: JSON.stringify({ userIds: ids }),
      });
      setData(updated);
      setPendingUsers([]);
      showSuccess(t('communities.usersAdded'));
    } catch (err: any) {
      showError(err?.message || 'Failed to add users');
    }
  }

  async function addManagers(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    const ids = pendingManagers.map((u) => u.id);
    try {
      const updated = await request<CommunityDto>(`/communities/${id}/managers`, {
        method: 'POST',
        body: JSON.stringify({ userIds: ids }),
      });
      setData(updated);
      setPendingManagers([]);
      showSuccess(t('communities.managersAdded'));
    } catch (err: any) {
      showError(err?.message || 'Failed to add managers');
    }
  }

  async function removeManager(userId: string) {
    if (!id) return;
    try {
      const updated = await request<CommunityDto>(`/communities/${id}/managers/${userId}`, {
        method: 'DELETE',
      });
      setData(updated);
      showSuccess(t('communities.managerRemoved'));
    } catch (err: any) {
      showError(err?.message || 'Failed to remove manager');
    }
  }

  async function addGroup(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    try {
      const created = await request<GroupDto>(`/communities/${id}/groups`, {
        method: 'POST',
        body: JSON.stringify({ name: groupName, groupType }),
      });
      setData((current) => current ? { ...current, groups: [...(current.groups || []), created] } : current);
      setGroupName('');
      setGroupType('STANDARD');
      showSuccess(t('communities.groupCreated'));
    } catch (err: any) {
      showError(err?.message || 'Failed to create group');
    }
  }

  if (loading) return <PageShell><div className="admin-panel p-5 text-sm text-slate-500" role="status">{t('communities.loading')}</div></PageShell>;
  if (error || !data) return <PageShell><div className="admin-panel p-5 text-sm text-red-700" role="alert">{error || t('communities.notFound')}</div></PageShell>;

  return (
    <PageShell>
      <PageHeader
        title={data.name}
        description={data.description || t('communities.description')}
        meta={<StatusBadge tone="violet">{t(`communities.type.${data.type}`)}</StatusBadge>}
        actions={(
          <>
            <button className="btn btn-neutral" onClick={() => navigate('/communities')}>{t('communities.back')}</button>
            <button className="btn btn-neutral" onClick={() => navigate(`/rides?${new URLSearchParams({ communityId: data.id })}`)}>{t('rides.filters.viewRides')}</button>
            <button className="btn btn-primary" onClick={() => navigate(`/communities/${id}/dashboard`)}>{t('communities.dashboard')}</button>
          </>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard label={t('communities.users')} value={data.users?.length ?? 0} tone="blue" />
        <MetricCard label={t('communities.managers')} value={data.managers?.length ?? 0} tone="violet" />
        <MetricCard label={t('communities.groupsSection')} value={data.groups?.length ?? 0} tone="green" />
      </div>

      <form onSubmit={saveCommunity} className="admin-panel mt-5 p-5">
        <div className="admin-section-heading">
          <div><h2>{t('communities.profile')}</h2><p>{t('communities.description')}</p></div>
          <button type="submit" className="btn btn-primary" disabled={savingCommunity || !communityName.trim()}>
            {savingCommunity ? t('communities.saving') : t('communities.save')}
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="admin-form-label" htmlFor="community-name">{t('communities.name')}</label>
            <input id="community-name" className="form-control mt-1 w-full" value={communityName} onChange={(event) => setCommunityName(event.target.value)} required />
          </div>
          <div>
            <label className="admin-form-label" htmlFor="community-type">{t('communities.type')}</label>
            <select id="community-type" className="form-control mt-1 w-full" value={communityType} onChange={(event) => setCommunityType(event.target.value)}>
              <option value="COMPANY">{t('communities.company')}</option>
              <option value="MUNICIPALITY">{t('communities.municipality')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="admin-form-label" htmlFor="community-description">{t('communities.descriptionField')}</label>
            <textarea id="community-description" className="form-control mt-1 w-full" rows={3} value={communityDescription} onChange={(event) => setCommunityDescription(event.target.value)} placeholder={t('communities.descriptionPlaceholder')} />
          </div>
        </div>
      </form>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <section className="admin-panel p-5">
          <div className="admin-section-heading">
            <div><h2>{t('communities.managers')}</h2><p>{t('communities.managersHint')}</p></div>
            <span className="admin-count-pill">{data.managers?.length ?? 0}</span>
          </div>
          <div className="admin-community-people">
            {data.managers?.map((user) => (
              <div key={user.id} className="admin-community-person">
                <span className="admin-person-avatar" aria-hidden="true">{(user.firstName || user.email).slice(0, 1).toLocaleUpperCase()}</span>
                <span className="min-w-0 flex-1"><strong>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}</strong><small>{user.email}</small></span>
                <button type="button" className="btn btn-ghost" onClick={() => removeManager(user.id)} title={t('communities.removeManager')} aria-label={`${t('communities.removeManager')}: ${user.email}`}>×</button>
              </div>
            ))}
            {!data.managers?.length && <p className="admin-community-empty-copy">{t('communities.noManagers')}</p>}
          </div>
          <form onSubmit={addManagers} className="admin-community-add-form">
            <UserTypeahead onSelect={(user) => setPendingManagers((prev) => prev.some((item) => item.id === user.id) ? prev : [...prev, user])} />
            {pendingManagers.length > 0 && <div className="admin-community-pending">{pendingManagers.map((user) => <span key={user.id} className="admin-community-chip">{user.email}<button type="button" aria-label={`Remove ${user.email}`} onClick={() => setPendingManagers((prev) => prev.filter((item) => item.id !== user.id))}>×</button></span>)}</div>}
            <button className="btn btn-primary" disabled={!pendingManagers.length}>{t('communities.addManagers')}</button>
          </form>
        </section>

        <section className="admin-panel p-5">
          <div className="admin-section-heading"><div><h2>{t('communities.users')}</h2><p>{t('communities.description')}</p></div><span className="admin-count-pill">{data.users?.length ?? 0}</span></div>
          <div className="admin-community-people admin-community-people-scroll">
            {data.users?.map((user) => (
              <div key={user.id} className="admin-community-person">
                <span className="admin-person-avatar" aria-hidden="true">{(user.firstName || user.email).slice(0, 1).toLocaleUpperCase()}</span>
                <span className="min-w-0 flex-1"><strong>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}</strong><small>{user.email}</small></span>
              </div>
            ))}
            {!data.users?.length && <p className="admin-community-empty-copy">{t('communities.noUsers')}</p>}
          </div>
          <form onSubmit={addUsers} className="admin-community-add-form">
            <UserTypeahead onSelect={(user) => setPendingUsers((prev) => prev.some((item) => item.id === user.id) ? prev : [...prev, user])} />
            {pendingUsers.length > 0 && <div className="admin-community-pending">{pendingUsers.map((user) => <span key={user.id} className="admin-community-chip">{user.email}<button type="button" aria-label={`Remove ${user.email}`} onClick={() => setPendingUsers((prev) => prev.filter((item) => item.id !== user.id))}>×</button></span>)}</div>}
            <button className="btn btn-primary" disabled={!pendingUsers.length}>{t('communities.addUsers')}</button>
          </form>
        </section>

        <section className="admin-panel p-5 xl:col-span-2">
          <div className="admin-section-heading"><div><h2>{t('communities.groupsSection')}</h2><p>{t('communities.description')}</p></div><span className="admin-count-pill">{data.groups?.length ?? 0}</span></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.groups?.map((group) => (
              <button key={group.id} type="button" onClick={() => navigate(`/communities/${id}/groups/${group.id}`)} className="admin-community-group">
                <span><strong>{group.name}</strong><small>{group.description || t('communities.noGroups')}</small></span>
                <span aria-hidden="true">↗</span>
              </button>
            ))}
            {!data.groups?.length && <p className="admin-community-empty-copy">{t('communities.noGroups')}</p>}
          </div>
          <form onSubmit={addGroup} className="admin-community-add-form sm:flex-row sm:items-end">
            <div className="w-full sm:max-w-md"><label className="admin-form-label" htmlFor="new-group-name">{t('communities.groupPlaceholder')}</label><input id="new-group-name" className="form-control mt-1 w-full" value={groupName} onChange={(event) => setGroupName(event.target.value)} required /></div>
            <div className="w-full sm:max-w-xs"><label className="admin-form-label" htmlFor="new-group-type">{t('communities.groupType')}</label><select id="new-group-type" className="form-control mt-1 w-full" value={groupType} onChange={(event) => setGroupType(event.target.value)}><option value="STANDARD">{t('communities.groupType.STANDARD')}</option><option value="KINDERGARTEN">{t('communities.groupType.KINDERGARTEN')}</option><option value="SCHOOL">{t('communities.groupType.SCHOOL')}</option></select></div>
            <button className="btn btn-primary" disabled={!groupName.trim()}>{t('communities.addGroup')}</button>
          </form>
        </section>
      </div>
    </PageShell>
  );
};
